import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, MessageCircle, User, ArrowRight, Plus, Mic } from 'lucide-react';
import * as onboardingApi from '../services/onboardingApi';
import { dispatchProfileCompletedEvent } from '../hooks/useOnboardingRedirect';
import { useFlowProtection } from '../../hooks/useFlowProtection';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'david';
  timestamp: Date;
}

const BackgroundGrid = () => (
  <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F8F9FB]">
    {/* Ellipse 1: Main Gradient Background */}
    <div
      className="absolute"
      style={{
        width: '2618.47px',
        height: '1939.34px',
        left: '-448.14px',
        top: '-919.31px',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
        transform: 'rotate(26.84deg)',
        transformOrigin: 'top left',
      }}
    />

    {/* Ellipse 2827: Blurred Overlay */}
    <div
      className="absolute hidden md:block"
      style={{
        width: '640px',
        height: '458px',
        left: '50%',
        top: '282px',
        transform: 'translateX(-50%) matrix(1, 0, 0, -1, 0, 0)',
        background: 'rgba(255, 255, 255, 0.4)',
        filter: 'blur(93.2px)',
        borderRadius: '53px',
      }}
    />

    {/* Grid Lines */}
    <div className="absolute inset-0 flex justify-center pointer-events-none">
      <div className="w-full h-full max-w-[1920px] relative">
        {/* Vertical Lines */}
        {[...Array(12)].map((_, i) => (
          <div
            key={`v-${i}`}
            className="absolute top-0 bottom-0 border-l border-dashed border-[rgba(40,40,42,0.2)]"
            style={{ left: `${105 + (i * 120)}px` }}
          />
        ))}

        {/* Horizontal Lines */}
        {[121, 300, 449, 627, 700, 872, 913, 1031].map((top, i) => (
          <div
            key={`h-${i}`}
            className="absolute left-0 right-0 border-t border-dashed border-[rgba(40,40,42,0.2)]"
            style={{ top: `${top}px` }}
          />
        ))}
      </div>
    </div>
  </div>
);

/**
 * OnboardingChat - Compact chat interface for completing profile during onboarding
 * Features onboarding background, skip button, and dynamic Dashboard button
 * Protected by flow protection - requires coming from career goals page or login redirect
 */
const OnboardingChat: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState<onboardingApi.MissingContentQuestion | null>(null);
  const [remainingQuestions, setRemainingQuestions] = useState<onboardingApi.MissingContentQuestion[]>([]);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [hasQuestions, setHasQuestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Flow protection - requires coming from career goals or login redirect
  const { isValidAccess } = useFlowProtection({
    storageKey: 'fromCareerGoals',
    allowIncompleteProfile: true,
    fallbackPath: '/candidate/dashboard'
  });

  useEffect(() => {
    loadOnboardingQuestions();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputMessage]);

  const loadOnboardingQuestions = async () => {
    try {
      setIsLoading(true);

      // Check if profile is already completed
      const userDataString = localStorage.getItem('authUser');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        const isProfileCompleted = userData.profile_completed || userData.profileCompleted;
        if (isProfileCompleted) {
          console.log('[OnboardingChat] Profile already completed, redirecting to dashboard');
          navigate('/candidate/dashboard');
          return;
        }
      }

      // Using /me endpoint - no userId needed, JWT token provides identity
      const response = await onboardingApi.getCurrentOnboardingStatus();
      const status = response.payload;

      const pending = status.missingQuestions.filter(q => q.status === 'pending');
      setRemainingQuestions(pending);

      if (pending.length > 0) {
        const firstQuestion = pending[0];
        setCurrentQuestion(firstQuestion);
        setShowSkipButton(!firstQuestion.isRequired);
        setHasQuestions(true);

        // Add intro message
        const introMessage: Message = {
          id: `david-intro-${Date.now()}`,
          text: "Hi! My name is David, and I'm excited to get to know you better.",
          sender: 'david',
          timestamp: new Date(Date.now() - 1000)
        };

        const questionMessage: Message = {
          id: `david-${Date.now()}`,
          text: firstQuestion.question,
          sender: 'david',
          timestamp: new Date()
        };
        setMessages([introMessage, questionMessage]);
      } else {
        // No questions, user can go to dashboard
        setHasQuestions(false);
        const completedMessage: Message = {
          id: `david-${Date.now()}`,
          text: "Great! You've completed your profile. You can now go to your dashboard!",
          sender: 'david',
          timestamp: new Date()
        };
        setMessages([completedMessage]);
      }
    } catch (error) {
      console.error('Failed to load onboarding questions:', error);
      setHasQuestions(false);
      const errorMessage: Message = {
        id: `david-${Date.now()}`,
        text: "I couldn't load your questions. You can proceed to your dashboard!",
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === '' || isLoading || !currentQuestion) return;

    const userMessageText = inputMessage.trim();
    setInputMessage('');
    if(textareaRef.current) textareaRef.current.style.height = 'auto';

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: userMessageText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      setIsLoading(true);

      // Using /me endpoint - no userId needed
      const response = await onboardingApi.answerCurrentMissingContentQuestion(
        currentQuestion.questionId,
        userMessageText
      );

      const result = response.payload;
      console.log('[OnboardingChat] Answer response:', result);

      const pending = result.missingQuestions.filter(q => q.status === 'pending');
      setRemainingQuestions(pending);

      // Check backend's allQuestionsCompleted flag
      if (result.allQuestionsCompleted || pending.length === 0) {
        // All questions answered
        console.log('[OnboardingChat] All questions completed');
        setHasQuestions(false);
        setCurrentQuestion(null);

        // Mark profile as completed using /me endpoint
        try {
          await onboardingApi.markCurrentProfileCompleted();
          console.log('[OnboardingChat] Profile marked as completed in backend');

          // Update localStorage
          const userDataString = localStorage.getItem('authUser');
          if (userDataString) {
            const userData = JSON.parse(userDataString);
            userData.profile_completed = true;
            userData.profileCompleted = true;
            localStorage.setItem('authUser', JSON.stringify(userData));
            console.log('[OnboardingChat] Updated localStorage with profile_completed = true');

            // Dispatch event to notify other components
            dispatchProfileCompletedEvent();
          }
        } catch (err) {
          console.error('[OnboardingChat] Error marking profile as completed:', err);
        }

        const completedMessage: Message = {
          id: `david-${Date.now()}`,
          text: "Perfect! Your profile is complete. You can now go to your dashboard!",
          sender: 'david',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, completedMessage]);
      } else if (pending.length > 0) {
        const nextQuestion = pending[0];
        setCurrentQuestion(nextQuestion);
        setShowSkipButton(!nextQuestion.isRequired);

        const davidMessage: Message = {
          id: `david-${Date.now()}`,
          text: nextQuestion.question,
          sender: 'david',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, davidMessage]);
      }
    } catch (error) {
      console.error('Failed to send answer:', error);
      const errorMessage: Message = {
        id: `david-${Date.now()}`,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipQuestion = async () => {
    if (!currentQuestion || isLoading) return;

    try {
      setIsLoading(true);

      // Using /me endpoint - no userId needed
      const response = await onboardingApi.skipCurrentMissingContentQuestion(
        currentQuestion.questionId
      );

      const result = response.payload;
      console.log('[OnboardingChat] Skip response:', result);

      const pending = result.missingQuestions.filter(q => q.status === 'pending');
      setRemainingQuestions(pending);

      // Check backend's allQuestionsCompleted flag
      if (result.allQuestionsCompleted || pending.length === 0) {
        // All questions handled
        console.log('[OnboardingChat] All questions completed (after skip)');
        setHasQuestions(false);
        setCurrentQuestion(null);

        // Mark profile as completed using /me endpoint
        try {
          await onboardingApi.markCurrentProfileCompleted();
          console.log('[OnboardingChat] Profile marked as completed in backend');

          // Update localStorage
          const userDataString = localStorage.getItem('authUser');
          if (userDataString) {
            const userData = JSON.parse(userDataString);
            userData.profile_completed = true;
            userData.profileCompleted = true;
            localStorage.setItem('authUser', JSON.stringify(userData));
            console.log('[OnboardingChat] Updated localStorage with profile_completed = true');

            // Dispatch event to notify other components
            dispatchProfileCompletedEvent();
          }
        } catch (err) {
          console.error('[OnboardingChat] Error marking profile as completed:', err);
        }

        const completedMessage: Message = {
          id: `david-${Date.now()}`,
          text: "Great! You can now go to your dashboard!",
          sender: 'david',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, completedMessage]);
      } else if (pending.length > 0) {
        const nextQuestion = pending[0];
        setCurrentQuestion(nextQuestion);
        setShowSkipButton(!nextQuestion.isRequired);

        const davidMessage: Message = {
          id: `david-${Date.now()}`,
          text: nextQuestion.question,
          sender: 'david',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, davidMessage]);
      }
    } catch (error) {
      console.error('Failed to skip question:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleDashboardClick = async () => {
    // Mark profile as completed before navigating to dashboard using /me endpoint
    try {
      await onboardingApi.markCurrentProfileCompleted();
      console.log('[OnboardingChat] Profile marked as completed via dashboard button');

      // Update localStorage
      const userDataString = localStorage.getItem('authUser');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        userData.profile_completed = true;
        userData.profileCompleted = true;
        localStorage.setItem('authUser', JSON.stringify(userData));
        console.log('[OnboardingChat] Updated localStorage with profile_completed = true');

        // Dispatch event to notify other components
        dispatchProfileCompletedEvent();
      }
    } catch (err) {
      console.error('[OnboardingChat] Error marking profile as completed:', err);
    }

    navigate('/candidate/dashboard');
  };

  // Don't render until flow access is validated
  if (isValidAccess === null) {
    return null;
  }

  if (isLoading && messages.length === 0) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col items-center justify-center overflow-hidden">
        <BackgroundGrid />
        <div className="relative z-10 text-center">
          <Loader2 className="w-12 h-12 text-[#015FA6] animate-spin mx-auto mb-4" />
          <p className="text-[#015FA6] text-lg font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col items-center justify-center overflow-hidden">
      <BackgroundGrid />

      {/* Main Chat Card - Compact Version */}
      <div className="relative z-10 w-full max-w-3xl h-[600px] bg-white rounded-3xl shadow-[0px_6px_12px_-4px_rgba(145,158,171,0.08)] flex flex-col overflow-hidden mx-4 border border-gray-100 animate-fade-in-up">

        {/* Chat Scroll Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 md:px-12 py-8 space-y-6">
          {messages.map((message) => {
            const isUser = message.sender === 'user';
            return (
              <div
                key={message.id}
                className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
              >
                <div className={`flex max-w-[85%] md:max-w-[70%] gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Avatar */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow-sm mt-auto mb-1 ${
                        isUser ? 'bg-[#015FA6] text-white' : 'bg-[#919191] text-white'
                    }`}>
                        {isUser ? <User size={18} /> : <MessageCircle size={18} />}
                    </div>

                    {/* Bubble */}
                    <div className="flex flex-col">
                        <div
                            className={`relative px-6 py-4 shadow-sm text-[15px] leading-relaxed ${
                            isUser
                                ? 'bg-[#015FA6] text-white rounded-2xl rounded-tr-sm'
                                : 'bg-[#F1F3F4] text-[#0B111E] rounded-2xl rounded-tl-sm'
                            }`}
                        >
                            {message.text}
                        </div>
                        <span className={`text-[11px] mt-1.5 px-1 ${
                            isUser ? 'text-right text-[#015FA6]/40' : 'text-left text-[#015FA6]/40'
                        }`}>
                            {formatTime(message.timestamp)}
                        </span>
                    </div>
                </div>
              </div>
            );
          })}

          {isLoading && messages.length > 0 && (
            <div className="flex justify-start animate-fade-in">
               <div className="flex max-w-[75%] gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#919191] text-white flex items-center justify-center shadow-sm mt-auto mb-1">
                      <MessageCircle size={18} />
                  </div>
                  <div className="bg-[#F1F3F4] text-[#0B111E] rounded-2xl rounded-tl-sm px-6 py-4">
                    <div className="flex items-center gap-1.5 h-5">
                      <div className="w-2 h-2 bg-[#015FA6]/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-[#015FA6]/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-[#015FA6]/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
               </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Compact */}
        <div className="p-4 bg-white relative z-20 border-t border-gray-50">
            <div className="max-w-4xl mx-auto">
                {/* Dashboard/Skip Actions */}
                <div className="flex justify-between items-center mb-2 px-2">
                    {!hasQuestions ? (
                        <button
                            onClick={handleDashboardClick}
                            className="text-sm font-semibold text-[#015FA6] hover:text-[#014f8c] flex items-center gap-2 transition-colors"
                        >
                            Go to Dashboard <ArrowRight size={16} />
                        </button>
                    ) : <div />}

                    {hasQuestions && showSkipButton && currentQuestion && (
                        <button
                            onClick={handleSkipQuestion}
                            disabled={isLoading}
                            className="text-xs font-semibold text-[#A5A5A5] hover:text-[#015FA6] transition-colors disabled:opacity-50"
                        >
                            Skip this question
                        </button>
                    )}
                </div>

                {/* Input Container - Compact */}
                <div className={`relative border border-[#E6E6E6] rounded-xl transition-all focus-within:border-[#015FA6]/50 focus-within:shadow-[0_0_0_4px_rgba(1,95,166,0.05)] ${!hasQuestions ? 'opacity-50 pointer-events-none' : ''}`}>
                    <textarea
                        ref={textareaRef}
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Type your message here..."
                        disabled={isLoading || !hasQuestions}
                        className="w-full bg-transparent px-4 py-3 resize-none focus:outline-none text-[#0B111E] placeholder:text-[#015FA6]/40 text-sm md:text-base min-h-[44px] max-h-[120px] rounded-xl"
                        style={{ fontFamily: '"SF Pro", sans-serif' }}
                    />

                    <div className="flex justify-between items-center px-2 pb-2 pt-0">
                        {/* Left Icons - Compact */}
                        <div className="flex items-center gap-2 px-1">
                            <button className="text-[#9CC1E8] hover:text-[#015FA6] transition-colors p-1.5 rounded-full hover:bg-[#015FA6]/5" disabled>
                                <Plus size={18} />
                            </button>
                            <button className="text-[#9CC1E8] hover:text-[#015FA6] transition-colors p-1.5 rounded-full hover:bg-[#015FA6]/5" disabled>
                                <Mic size={18} />
                            </button>
                        </div>

                        {/* Send Button - Compact */}
                        <button
                            onClick={handleSendMessage}
                            disabled={inputMessage.trim() === '' || isLoading}
                            className="w-8 h-8 bg-[#919191] hover:bg-[#015FA6] disabled:bg-[#E0E0E0] disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-all shadow-sm transform active:scale-95"
                        >
                            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} className="ml-0.5" />}
                        </button>
                    </div>
                </div>

                {hasQuestions && (
                    <p className="text-center text-[10px] text-[#A5A5A5] mt-3">
                        {remainingQuestions.length} question{remainingQuestions.length !== 1 ? 's' : ''} remaining
                    </p>
                )}
            </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap');

        body {
          font-family: 'Manrope', sans-serif;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #E6E6E6;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #CBCBCB;
        }

        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default OnboardingChat;
